import { useState } from 'react';
import { first } from 'lodash';
import classNames from 'classnames';

import { Form } from '@/components/form';
import { SearchInput } from '@/components/form/search-input';
import { Button } from '@/components/button';

import type { Field, Model, PipelineStateRelation, SwitchToRelationOperation } from '../types';
import { useDirtyContext } from '../dirty-context';
import { findValidRelation } from '../pipeline/state';
import { createRelationsOptions } from '../components/select-options/relation';
import {
  constructRelationOptionValue,
  deconstructRelationOptionValue,
} from '../components/select-options/utils';

import form from '@/components/form/form.module.scss';
import styles from './pipeline.module.scss';

interface SwitchToRelationFormProps {
  fields: Field[];
  model: Model;
  models: Model[];
  operation?: SwitchToRelationOperation;
  setOperation(operation: SwitchToRelationOperation): void;
  onClose(): void;
  relations: PipelineStateRelation[];
}

export const SwitchToRelationForm = (props: SwitchToRelationFormProps) => {
  const { fields, models, relations, operation } = props;
  const relationOptions = createRelationsOptions(relations, fields, models);
  const defaultOptionValue = first(relationOptions)?.value;
  const parameters = operation?.parameters;

  const relation =
    parameters !== undefined
      ? findValidRelation(relations, models, parameters.relation.key, parameters?.relation.modelId)
      : undefined;

  const initialOptionValue =
    relation !== undefined
      ? constructRelationOptionValue({
          key: relation.key,
          baseModelId: relation.baseModelId,
        })
      : defaultOptionValue;
  const [optionValue, setOptionValue] = useState(initialOptionValue);
  const { setDirty } = useDirtyContext();

  if (optionValue === undefined) {
    return (
      <div className={form.formHorizontal}>
        <div>No available relations</div>
        <div>
          <Button onClick={props.onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const handleChange = (value: string) => {
    setOptionValue(value);
    const isDirty = initialOptionValue !== value;
    setDirty(isDirty);
  };

  const handleSubmit = () => {
    setDirty(false);
    const reference = deconstructRelationOptionValue(optionValue);
    if (!('key' in reference)) {
      throw new Error('Invalid option value');
    }
    props.setOperation({
      operation: 'switchToRelation',
      parameters: { relation: { key: reference.key, modelId: reference.baseModelId } },
    });
  };

  const handleCancel = () => {
    setDirty(false);
    props.onClose();
  };

  return (
    <Form
      className={classNames(form.formHorizontal, styles.switchRelation)}
      onSubmit={handleSubmit}>
      <div className={form.formRow}>
        <label className={classNames(form.formLabel, styles.switchRelationLabel)}>From</label>
        <SearchInput
          size="small"
          options={relationOptions}
          value={optionValue}
          onChange={(option) => {
            if (option !== null) {
              handleChange(option);
            }
          }}
          autoFocus
        />
      </div>

      <div className={form.formControls}>
        <Button size="small" type="submit">
          {props.operation ? 'Save' : 'Jump to'}
        </Button>
        <Button size="small" variant="outlined" onClick={handleCancel}>
          {props.operation ? 'Cancel' : 'Back'}
        </Button>
      </div>
    </Form>
  );
};
